import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, projectStagesAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DollarSign, TrendingUp, Receipt, CreditCard, ArrowDownRight, FilePlus2, Loader2, ArrowUpDown, Search, X, Building2, AlertTriangle, Download, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, StageBadge } from './ProjectsList';

export default function ProjectFinancials() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('all');
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('receivable');
  const [sortDir, setSortDir] = useState('desc');
  const [stageOptions, setStageOptions] = useState([]);
  const [stageConfig, setStageConfig] = useState({});

  // Load stages from API
  useEffect(() => {
    projectStagesAPI.list().then(res => {
      const s = res.data.stages || [];
      setStageOptions(s);
      const cfg = {};
      s.forEach(st => { cfg[st.slug] = { label: st.name, color: st.color, id: st.id }; });
      setStageConfig(cfg);
    }).catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (stage !== 'all') params.stage = stage;
      const res = await projectsAPI.financialReport(params);
      setData(res.data);
    } catch {
      toast.error('Failed to load financial report');
    } finally {
      setLoading(false);
    }
  }, [stage]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const filteredProjects = useMemo(() => {
    if (!data?.projects) return [];
    let rows = [...data.projects];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(p =>
        p.job_name?.toLowerCase().includes(s) ||
        p.customer_name?.toLowerCase().includes(s) ||
        p.job_number?.toLowerCase().includes(s)
      );
    }
    rows.sort((a, b) => {
      const av = a[sortCol] ?? -Infinity;
      const bv = b[sortCol] ?? -Infinity;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return rows;
  }, [data, search, sortCol, sortDir]);

  const t = data?.totals || {};

  const exportCSV = () => {
    if (!filteredProjects.length) return;
    const headers = ['Project Name', 'Project Number', 'Customer', 'Stage', 'Original Contract', 'Approved Change Orders', 'Adjusted Contract', 'Budget Cost', 'Margin %', 'Invoiced', 'Collected', 'Receivable'];
    const escape = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const num = (v) => v ? Number(v).toFixed(2) : '0.00';
    const rows = filteredProjects.map(p => [
      escape(p.job_name), escape(p.job_number || ''), escape(p.customer_name || ''),
      escape(p.project_stage || ''), num(p.original_contract), num(p.co_approved),
      num(p.adjusted_contract), num(p.budget_cost), p.margin_pct !== null ? p.margin_pct.toFixed(1) : '',
      num(p.invoiced), num(p.collected), num(p.receivable),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-financials${stage !== 'all' ? `-${stage}` : ''}${search ? `-${search.replace(/\s+/g, '_')}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredProjects.length} project${filteredProjects.length !== 1 ? 's' : ''} to CSV`);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="project-financials-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Project Financials</h1>
            <p className="text-sm text-muted-foreground mt-1">Cross-project financial health at a glance</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search projects or customers..."
              className="pl-9 h-9"
              data-testid="financial-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="w-[160px] h-9" data-testid="stage-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {stageOptions.map(s => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {!loading && filteredProjects.length > 0 && (
            <Button size="sm" variant="outline" className="h-9" onClick={exportCSV} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="financial-summary-cards">
              <TotalCard icon={DollarSign} label="Original Contract" value={formatCurrency(t.original_contract)} />
              <TotalCard icon={FilePlus2} label="Change Orders" value={t.co_approved ? formatCurrency(t.co_approved) : '—'} />
              <TotalCard icon={TrendingUp} label="Adjusted Contract" value={formatCurrency(t.adjusted_contract)} />
              <TotalCard icon={Receipt} label="Invoiced" value={formatCurrency(t.invoiced)} accent="blue" />
              <TotalCard icon={CreditCard} label="Collected" value={formatCurrency(t.collected)} accent="green" />
              <TotalCard icon={ArrowDownRight} label="Receivable" value={t.receivable > 0 ? formatCurrency(t.receivable) : '—'} accent={t.receivable > 0 ? 'amber' : null} />
            </div>

            {/* Cost Type Breakdown */}
            {data?.cost_breakdown?.length > 0 && (
              <CostBreakdown items={data.cost_breakdown} totalBudget={t.budget_cost} />
            )}

            {/* Projects Table */}
            <div className="border rounded-lg overflow-hidden" data-testid="financial-table">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-[240px]">Project</TableHead>
                      <TableHead className="w-[120px]">Stage</TableHead>
                      <SortHeader col="original_contract" label="Contract" current={sortCol} dir={sortDir} toggle={toggleSort} />
                      <SortHeader col="co_approved" label="COs" current={sortCol} dir={sortDir} toggle={toggleSort} />
                      <SortHeader col="adjusted_contract" label="Adjusted" current={sortCol} dir={sortDir} toggle={toggleSort} />
                      <SortHeader col="budget_cost" label="Budget Cost" current={sortCol} dir={sortDir} toggle={toggleSort} />
                      <SortHeader col="margin_pct" label="Margin" current={sortCol} dir={sortDir} toggle={toggleSort} />
                      <SortHeader col="invoiced" label="Invoiced" current={sortCol} dir={sortDir} toggle={toggleSort} />
                      <SortHeader col="collected" label="Collected" current={sortCol} dir={sortDir} toggle={toggleSort} />
                      <SortHeader col="receivable" label="Receivable" current={sortCol} dir={sortDir} toggle={toggleSort} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-10 text-muted-foreground">
                          {search ? 'No projects match your search' : 'No projects found'}
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredProjects.map(p => {
                      const needsAttention = (p.receivable > 0 && p.receivable > p.adjusted_contract * 0.5) ||
                        (p.margin_pct !== null && p.margin_pct < 10) ||
                        (p.adjusted_contract > 0 && p.invoiced === 0);
                      return (
                        <TableRow
                          key={p.id}
                          className="cursor-pointer hover:bg-muted/30"
                          onClick={() => navigate(`/jobs/projects/${p.id}`)}
                          data-testid={`project-row-${p.id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {needsAttention && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{p.job_name}</p>
                                {p.customer_name && <p className="text-xs text-muted-foreground truncate">{p.customer_name}</p>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><StageBadge stage={p.project_stage} stageConfig={stageConfig} /></TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{p.original_contract ? formatCurrency(p.original_contract) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{p.co_approved ? formatCurrency(p.co_approved) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm font-medium">{p.adjusted_contract ? formatCurrency(p.adjusted_contract) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{p.budget_cost ? formatCurrency(p.budget_cost) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {p.margin_pct !== null ? (
                              <span className={p.margin_pct < 10 ? 'text-red-600 font-medium' : p.margin_pct >= 30 ? 'text-green-600' : ''}>
                                {p.margin_pct}%
                              </span>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{p.invoiced ? formatCurrency(p.invoiced) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{p.collected ? formatCurrency(p.collected) : '—'}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {p.receivable > 0 ? (
                              <span className="text-amber-600 font-medium">{formatCurrency(p.receivable)}</span>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {/* Table footer with count */}
              <div className="px-4 py-2 bg-muted/20 border-t text-xs text-muted-foreground flex items-center justify-between">
                <span>{filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}{search ? ' (filtered)' : ''}</span>
                <span>{t.project_count} total across all stages</span>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function TotalCard({ icon: Icon, label, value, accent }) {
  const accentStyles = {
    blue: 'border-blue-200 dark:border-blue-800/50 bg-blue-50/40 dark:bg-blue-950/20 shadow-sm',
    green: 'border-green-200 dark:border-green-800/50 bg-green-50/40 dark:bg-green-950/20 shadow-sm',
    amber: 'border-amber-200 dark:border-amber-800/50 bg-amber-50/40 dark:bg-amber-950/20 shadow-sm',
  };
  const iconAccent = {
    blue: 'text-blue-500', green: 'text-green-500', amber: 'text-amber-500',
  };
  return (
    <div className={`border rounded-lg p-3 ${accent ? accentStyles[accent] || '' : ''}`} data-testid={`total-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className={`w-3.5 h-3.5 ${accent ? iconAccent[accent] || 'text-muted-foreground' : 'text-muted-foreground'}`} />
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums">{value ?? '—'}</p>
    </div>
  );
}

function SortHeader({ col, label, current, dir, toggle }) {
  const active = current === col;
  return (
    <TableHead className="text-right cursor-pointer select-none hover:bg-muted/20" onClick={() => toggle(col)}>
      <div className="flex items-center justify-end gap-1">
        <span>{label}</span>
        <ArrowUpDown className={`w-3 h-3 ${active ? 'text-foreground' : 'text-muted-foreground/40'}`} />
      </div>
    </TableHead>
  );
}

const COST_TYPE_BAR_COLORS = {
  labor: 'bg-blue-500',
  materials: 'bg-emerald-500',
  equipment: 'bg-amber-500',
  subcontractor: 'bg-purple-500',
  other: 'bg-slate-400',
  permits: 'bg-cyan-500',
  uncategorized: 'bg-gray-300 dark:bg-gray-600',
};

const COST_TYPE_DOT_COLORS = {
  labor: 'bg-blue-500',
  materials: 'bg-emerald-500',
  equipment: 'bg-amber-500',
  subcontractor: 'bg-purple-500',
  other: 'bg-slate-400',
  permits: 'bg-cyan-500',
  uncategorized: 'bg-gray-300 dark:bg-gray-600',
};

function getBarColor(name) {
  if (!name) return COST_TYPE_BAR_COLORS.uncategorized;
  return COST_TYPE_BAR_COLORS[name.toLowerCase()] || 'bg-indigo-400';
}

function CostBreakdown({ items, totalBudget }) {
  const maxCost = Math.max(...items.map(i => i.total_cost), 1);

  return (
    <div className="border rounded-lg p-4" data-testid="cost-breakdown-section">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Budget by Cost Type</h3>
        {totalBudget > 0 && (
          <span className="text-xs text-muted-foreground ml-auto">Total: {formatCurrency(totalBudget)}</span>
        )}
      </div>

      {/* Stacked bar */}
      {totalBudget > 0 && (
        <div className="flex h-3 rounded-full overflow-hidden mb-4" data-testid="cost-breakdown-bar">
          {items.map((item, i) => {
            const pct = (item.total_cost / totalBudget) * 100;
            if (pct < 0.5) return null;
            return (
              <div
                key={item.cost_type_id || 'uncat'}
                className={`${getBarColor(item.cost_type_name)} transition-all ${i === 0 ? '' : 'border-l border-white/30'}`}
                style={{ width: `${pct}%` }}
                title={`${item.cost_type_name}: ${formatCurrency(item.total_cost)} (${pct.toFixed(1)}%)`}
              />
            );
          })}
        </div>
      )}

      {/* Legend / Detail rows */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
        {items.map(item => {
          const pct = totalBudget > 0 ? ((item.total_cost / totalBudget) * 100).toFixed(1) : 0;
          const barWidth = totalBudget > 0 ? (item.total_cost / maxCost) * 100 : 0;
          return (
            <div key={item.cost_type_id || 'uncat'} className="flex items-center gap-3" data-testid={`cost-breakdown-item-${item.cost_type_name?.toLowerCase() || 'uncategorized'}`}>
              <div className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${getBarColor(item.cost_type_name)}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{item.cost_type_name}</span>
                  <span className="text-sm tabular-nums font-semibold flex-shrink-0">{formatCurrency(item.total_cost)}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${getBarColor(item.cost_type_name)}`} style={{ width: `${barWidth}%` }} />
                  </div>
                  <span className="text-[10px] text-muted-foreground tabular-nums w-10 text-right">{pct}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
